import { useState, type FormEvent, type ReactNode } from "react";
import type { AccountPatch, RegisteredAccount } from "../../types/account";
import { useServerAnswer } from "../../hooks/useServerAnswer";
import { formatLongDate } from "../../utils/dates";
import { plainFigure } from "../../utils/figure";
import { isOwedAccount } from "./accountFacts";
import { RefusalSentence } from "./RefusalSentence";

interface OpeningQuestionProps {
  account: RegisteredAccount;
  onAnswer: (body: AccountPatch) => Promise<void>;
}

// backend/prime/accounts/opening_words.py OPENING_DATE_EARLY: answered by Confirm.
const DATE_EARLY = "OPENING_DATE_EARLY";
const ZERO_LABEL = "This account was new and started at zero";

/**
 * The question in the client's words; the card's asks what was owed (LF-74's
 * wording). With no date chosen yet (a card is never predicted) the same
 * words end without "on <date>"; the date is spelled out (walk-through round 1).
 */
function questionText(account: RegisteredAccount, date: string): string {
  const on = date === "" ? "" : ` on ${formatLongDate(date)}`;
  return isOwedAccount(account)
    ? `What did you owe on this card${on}?`
    : `How much was in this account${on}?`;
}

/** The PATCH body of one answer: the figure or the explicit zero, the date, the revision. */
function openingBody(account: RegisteredAccount, answer: OpeningAnswer): AccountPatch {
  const body: AccountPatch = {
    opening_as_of: answer.asOf,
    expected_revision: account.opening_revision,
  };
  if (answer.zero) body.new_account_zero = true;
  else body.opening_balance = plainFigure(answer.amount);
  return body;
}

interface OpeningAnswer {
  amount: string;
  asOf: string;
  zero: boolean;
}

/**
 * 1(c): the starting balance, prefilled from the prediction. The prediction's
 * basis is not shown: the server serves it as a stored key, not a sentence
 * (gate 2 Risk H1). Every PATCH carries `expected_revision`;
 * on OPENING_DATE_EARLY the server's sentence is shown with a Confirm that
 * sends the same answer again with `confirm_early_date`.
 */
export function OpeningQuestion({ account, onAnswer }: OpeningQuestionProps): ReactNode {
  const [answer, setAnswer] = useState<OpeningAnswer>({
    amount: account.predicted_opening ?? "",
    asOf: account.predicted_as_of ?? "",
    zero: false,
  });
  const { busy, refusal, run, clear } = useServerAnswer(onAnswer);
  const question = questionText(account, answer.asOf);

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    void run(openingBody(account, answer));
  }

  return (
    <form className="account-question" onSubmit={handleSubmit}>
      <label htmlFor={`opening-amount-${account.id}`}>{question}</label>
      <input
        id={`opening-amount-${account.id}`}
        type="text"
        inputMode="decimal"
        value={answer.amount}
        onChange={(e) => setAnswer({ ...answer, amount: e.target.value })}
        disabled={busy || answer.zero}
      />
      <input
        type="date"
        aria-label={question}
        value={answer.asOf}
        onChange={(e) => setAnswer({ ...answer, asOf: e.target.value })}
        disabled={busy}
      />
      <label className="account-zero">
        <input
          type="checkbox"
          checked={answer.zero}
          onChange={(e) => setAnswer({ ...answer, zero: e.target.checked })}
          disabled={busy}
        />
        {ZERO_LABEL}
      </label>
      <button type="submit" disabled={busy || answer.asOf === ""}>
        Save
      </button>
      <RefusalSentence refusal={refusal} onDismiss={clear}>
        {refusal?.code === DATE_EARLY && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void run({ ...openingBody(account, answer), confirm_early_date: true })}
          >
            Confirm
          </button>
        )}
      </RefusalSentence>
    </form>
  );
}
